import Vue from 'vue';
import Vuetify from 'vuetify';
import 'vuetify/dist/vuetify.min.css';
import de from 'vuetify/src/locale/de';
import '@mdi/font/css/materialdesignicons.css';
import {readThemePreference, resolveDark} from '@/helpers/themePreference.helper';

Vue.use(Vuetify);

/**
 * Palette für Statistik-Grafiken. Bewusst unabhängig von `primary`: Die Markenfarbe ist ein
 * heller Gelbton ohne Aussagekraft und mit zu geringem Kontrast für Diagrammflächen.
 *
 * Kräftige Töne wie im Dashboard-Widget „Offene Standorte", aber auf Kontrast gestellt: Die
 * fünf Rollen sind gegen beide Flächen der App geprüft (hell #FFFFFF, dunkel #1E1E1E) und
 * gelten in beiden Themes unverändert - der dunkle Modus ist geprüft, nicht gespiegelt.
 *
 * Farbfehlsichtigkeit (Protan/Deutan, OKLab-DeltaE) besteht in beiden Modi mit deutlichem
 * Abstand: schlechtestes benachbartes Paar DeltaE 9.5 im Ausfall-Stapel und 12.4 im
 * Vertretungs-Stapel (Ziel >= 8), normalsichtig 21.0 bzw. 22.7 (Grenze >= 15).
 *
 * Grün und Rot liegen nie nebeneinander: „abgesagt" ist eine bewusste Entscheidung
 * (vizCaution), „abgelaufen" der eigentliche Fehlschlag (vizBad). Genau dieses Paar bricht
 * bei Rot-Grün-Sehschwäche sonst zusammen.
 *
 * `vizCaution` erreicht auf weißer Fläche nur 2.26:1. Das ist bewusst in Kauf genommen -
 * ein kontrastreicheres Amber wäre stumpf. Zu jedem Diagramm gehört deshalb eine Tabelle
 * mit denselben Werten, und in Legenden steht neben der Farbe immer die Beschriftung.
 *
 * `vizNeutral` unterschreitet die Chroma-Schwelle absichtlich: Die Rolle soll grau wirken,
 * weil sie ausdrücklich keine Wertung trägt.
 */
const vizColors = {
    /** Positiver Ausgang: Vertretung gefunden, tatsächlich vertreten. */
    vizGood: '#00a878',
    /** Bewusste Gegenentscheidung: Unterricht abgesagt, Person nicht erreichbar. */
    vizCaution: '#ed9b06',
    /** Fehlschlag: abgelaufen, abgelehnt. */
    vizBad: '#e5383b',
    /** Ohne Wertung: noch offen, keine Reaktion. */
    vizNeutral: '#7d8a93',
    /** Sachliche Hervorhebung ohne Gut/Schlecht-Bedeutung: Zusagen, Ausfalltage, Auswahl. */
    vizAccent: '#4263eb',
};

export default new Vuetify({
    theme: {
        // Last choice on this device (Hell / Dunkel / System), so there is no light flash before the login is known
        dark: resolveDark(readThemePreference()),
        // dark: true,
        options: {
            customProperties: true,
        },
        themes: {
            light: {
                ...vizColors,
                primary: '#f2e791',
                // primary: '#e5da92',
                tabs: '#616161',
                nav: '#f2e791',
                navContent: '#332f20',
                search: '#455A64',
                secondary: '#424242',
                accent: '#82B1FF',
                error: '#FF5252',
                info: '#2196F3',
                warning: '#FFC107',
                anchor: '#2196F3',
                whatsApp: {
                    base: '#25d366',
                    darken1: '#24b35d',
                    darken2: '#198042',
                    darken3: '#134d26',
                    darken4: '#0d260d',
                    lighten1: '#47ed87',
                    lighten2: '#80ffb9',
                    lighten3: '#b3ffd5',
                    lighten4: '#d9ffe9',
                },
                outlook: {
                    base: '#127CD6',
                    darken1: '#0e66b3',
                    darken2: '#0d5491',
                    darken3: '#0b4170',
                    darken4: '#082e4f',
                    lighten1: '#4aa7f7',
                    lighten2: '#80c4ff',
                    lighten3: '#b3dbff',
                    lighten4: '#d9f2ff',
                },
            },
            dark: {
                ...vizColors,
                primary: '#a6a679',
                anchor: '#ffffff',
            },
        },
    },
    lang: {
        locales: {de},
        current: 'de',
    },
});
